import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useParams } from "react-router-dom";

import { DatasetEditorContext } from "..";

import { ENDPOINTS } from "@/api/application-api";
import { useChannel } from "@/components/Messaging/useChannel";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useNavigateWithSearch } from "@/hooks";
import { displayErrorToast, displayToast } from "@/utils/toast";

export const useSaveProgress = () => {
  const { user } = useUser();

  const context = useContext(DatasetEditorContext);
  const folderId = context?.datasetGroup?.folder_id;
  const queryClient = useQueryClient();
  const navigateWithSearch = useNavigateWithSearch();
  const { datasetGroupId, workspaceId } = useParams();

  useChannel(
    `user:${user?.id}`,
    WEBSOCKET_MESSAGE_NAME.DATASET_FILE_PROCESSING_PROGRESS,
    socketMessage => {
      const data = JSON.parse(socketMessage.data);

      if (data.payload?.type === "dataset_save_validation" && context?.processingType === "save") {
        context?.setProcessingProgress?.(data.payload.progress);
      }
    },
  );

  useChannel(
    `user:${user?.id}`,
    WEBSOCKET_MESSAGE_NAME.DATASET_FILE_PROCESSING_COMPLETE,
    async socketMessage => {
      const data = JSON.parse(socketMessage.data);

      if (data.type === "dataset_save" && context?.processingType === "save") {
        context?.setProcessingType?.(null);
        context?.setProcessingProgress?.(0);

        if (data.success) {
          await queryClient.invalidateQueries({
            queryKey: [ENDPOINTS.dataset_groups],
          });
          await queryClient.invalidateQueries({
            queryKey: [ENDPOINTS.datasets],
          });
          await queryClient.invalidateQueries({
            queryKey: [ENDPOINTS.dataset_rows],
          });
          queryClient.invalidateQueries({
            queryKey: ["folder-entities", workspaceId, folderId ?? null],
          });

          displayToast("Dataset saved successfully", ToastType.success);

          if (data.dataset_id) {
            navigateWithSearch(
              `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${data.dataset_id}`,
            );
          }
        } else {
          displayErrorToast(data.message || "Failed to save dataset");
        }
      }
    },
  );
};
